
import { isHintInsight } from './utils';

const TAG_MERGE_TYPE = 'tag_merge';

/**
 * Simple string similarity check (normalized edit distance)
 * If minThreshold is provided, skip edit distance when length alone proves
 * the strings cannot reach that similarity.
 */
export function getSimilarity(s1, s2, minThreshold = 0, isNormalized = false) {
  if (!s1 || !s2) return 0;
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;

  if (minThreshold > 0 && (shorter.length / longerLength) < minThreshold - 1e-9) {
    return 0;
  }

  return (longerLength - editDistance(longer, shorter, isNormalized)) / longerLength;
}

function editDistance(s1, s2, isNormalized = false) {
  if (!isNormalized) {
    s1 = s1.toLowerCase();
    s2 = s2.toLowerCase();
  }
  const costs = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}

/**
 * Detect duplicate Notes (Phase A: Rule-based)
 */
export function detectDuplicateNotes(notes) {
  const duplicates = [];
  const activeNotes = notes.filter(n => n.status !== 'archived');

  // Pre-calculate to avoid redundant processing in O(N^2) loop
  const lowTitles = activeNotes.map(n => (n.title || '').toLowerCase());
  const roomSets = activeNotes.map(n => new Set(n.sourceRoomIds || []));

  for (let i = 0; i < activeNotes.length; i++) {
    const noteA = activeNotes[i];
    const roomSetA = roomSets[i];
    const lowTitleA = lowTitles[i];

    for (let j = i + 1; j < activeNotes.length; j++) {
      const noteB = activeNotes[j];
      const roomSetB = roomSets[j];
      const lowTitleB = lowTitles[j];
      let reason = null;

      // 1. Same sourceRoomId (O(M) Set lookup instead of O(M*K) filter/includes)
      let hasCommonRoom = false;
      if (roomSetA.size > 0 && roomSetB.size > 0) {
        for (const roomId of roomSetA) {
          if (roomSetB.has(roomId)) {
            hasCommonRoom = true;
            break;
          }
        }
      }
      if (hasCommonRoom) {
        reason = `同じルームから作成された類似Noteです。`;
      }

      // 2. Similar title (Using pre-normalized titles)
      if (!reason && getSimilarity(lowTitleA, lowTitleB, 0.8, true) > 0.8) {
        reason = `タイトルが非常に類似しています。`;
      }

      if (reason) {
        // Decide targetId (the one to keep). Usually the one with more content or more recent.
        // For now, keep the one created earlier as target, or the one with more tags.
        const keepA = (noteA.tags?.length || 0) >= (noteB.tags?.length || 0);
        const target = keepA ? noteA : noteB;
        const source = keepA ? noteB : noteA;

        duplicates.push({
          type: "duplicate_note",
          title: `重複候補: 「${noteA.title}」と「${noteB.title}」`,
          summary: reason,
          targetType: "note",
          targetId: target.id,
          proposedPatch: {
            mergeFrom: source.id,
            action: "archive_source"
          }
        });
      }
    }
  }
  return duplicates;
}

/**
 * Detect duplicate Insights (Phase A: Rule-based)
 */
export function detectDuplicateInsights(insights) {
  const duplicates = [];
  // Proposed Changes are operations, not normal Hint candidates to deduplicate.
  const activeInsights = insights.filter(i =>
    i.status === 'suggested' &&
    isHintInsight(i)
  );

  // Pre-calculate to avoid redundant processing in O(N^2) loop
  const lowTitles = activeInsights.map(i => (i.title || '').toLowerCase());
  const sortedTags = activeInsights.map(i => [...(i.tags || [])].sort().join(','));
  const noteSets = activeInsights.map(i => new Set(i.sourceNoteIds || []));

  for (let i = 0; i < activeInsights.length; i++) {
    const insightA = activeInsights[i];
    const noteSetA = noteSets[i];
    const sortedTagsA = sortedTags[i];
    const lowTitleA = lowTitles[i];

    for (let j = i + 1; j < activeInsights.length; j++) {
      const insightB = activeInsights[j];
      const noteSetB = noteSets[j];
      const sortedTagsB = sortedTags[j];
      const lowTitleB = lowTitles[j];
      let reason = null;

      // 1. Same sourceNoteIds and same suggestedTargetType
      if (insightA.suggestedTargetType === insightB.suggestedTargetType && noteSetA.size > 0 && noteSetA.size === noteSetB.size) {
        let allSame = true;
        for (const noteId of noteSetA) {
          if (!noteSetB.has(noteId)) {
            allSame = false;
            break;
          }
        }
        if (allSame) {
          reason = `同じNote群から抽出された同じ種類のHint候補です。`;
        }
      }

      // 2. Same tags and suggestedTargetType
      if (!reason && insightA.suggestedTargetType === insightB.suggestedTargetType && sortedTagsA !== "" && sortedTagsA === sortedTagsB) {
        reason = `同じ種類でタグ設定が完全に一致しています。`;
      }

      // 3. Similar title
      if (!reason && insightA.suggestedTargetType === insightB.suggestedTargetType && getSimilarity(lowTitleA, lowTitleB, 0.8, true) > 0.8) {
        reason = `タイトルが非常に類似しています。`;
      }

      if (reason) {
        // Keep the one with higher confidence or longer summary
        const keepA = (insightA.confidence || 0) >= (insightB.confidence || 0);
        const target = keepA ? insightA : insightB;
        const source = keepA ? insightB : insightA;

        duplicates.push({
          type: "duplicate_insight",
          title: `重複候補: 「${insightA.title}」と「${insightB.title}」`,
          summary: reason,
          targetType: "insight",
          targetId: target.id,
          proposedPatch: {
            mergeFrom: source.id,
            action: "reject_source"
          }
        });
      }
    }
  }
  return duplicates;
}

/**
 * Detect duplicate Assets (Phase A: Rule-based)
 */
export function detectDuplicateAssets(assets) {
  const duplicates = [];
  const activeAssets = assets.filter(a => a.status !== 'archived' && a.status !== 'deprecated');

  // Pre-calculate to avoid redundant processing in O(N^2) loop
  const lowTitles = activeAssets.map(a => (a.title || '').toLowerCase());
  const sortedTags = activeAssets.map(a => [...(a.tags || [])].sort().join(','));
  const noteSets = activeAssets.map(a => new Set(a.sourceNoteIds || []));

  for (let i = 0; i < activeAssets.length; i++) {
    const assetA = activeAssets[i];
    const noteSetA = noteSets[i];
    const sortedTagsA = sortedTags[i];
    const lowTitleA = lowTitles[i];

    for (let j = i + 1; j < activeAssets.length; j++) {
      const assetB = activeAssets[j];
      const noteSetB = noteSets[j];
      const sortedTagsB = sortedTags[j];
      const lowTitleB = lowTitles[j];
      let reason = null;

      // 1. Same sourceNoteIds
      if (assetA.type === assetB.type && noteSetA.size > 0 && noteSetB.size > 0) {
        let hasCommonNote = false;
        for (const noteId of noteSetA) {
          if (noteSetB.has(noteId)) {
            hasCommonNote = true;
            break;
          }
        }
        if (hasCommonNote) {
          reason = `同じNoteから作成された同じ種類のAssetです。`;
        }
      }

      // 2. Same tags and type
      if (!reason && assetA.type === assetB.type && sortedTagsA !== "" && sortedTagsA === sortedTagsB) {
        reason = `同じ種類でタグ設定が完全に一致しています。`;
      }

      // 3. Similar title
      if (!reason && assetA.type === assetB.type && getSimilarity(lowTitleA, lowTitleB, 0.8, true) > 0.8) {
        reason = `タイトルが非常に類似しています。`;
      }

      if (reason) {
        const keepA = (assetA.maturity === 'growing' && assetB.maturity === 'seed') || (assetA.body?.length || 0) >= (assetB.body?.length || 0);
        const target = keepA ? assetA : assetB;
        const source = keepA ? assetB : assetA;

        duplicates.push({
          type: "duplicate_asset",
          title: `重複候補: 「${assetA.title}」と「${assetB.title}」`,
          summary: reason,
          targetType: "asset",
          targetId: target.id,
          proposedPatch: {
            mergeFrom: source.id,
            action: "archive_source"
          }
        });
      }
    }
  }
  return duplicates;
}

function normalizeTagForComparison(tag) {
  return String(tag || '')
    .normalize('NFKC')
    .trim()
    .toLowerCase()
    .replace(/[\u3041-\u3096]/g, char => String.fromCharCode(char.charCodeAt(0) + 0x60))
    .replace(/[\s\u3000_\-/／|・.。]+/g, '');
}

function normalizeSmallKana(char) {
  const smallToLarge = {
    'ァ': 'ア',
    'ィ': 'イ',
    'ゥ': 'ウ',
    'ェ': 'エ',
    'ォ': 'オ',
    'ャ': 'ヤ',
    'ュ': 'ユ',
    'ョ': 'ヨ',
    'ッ': 'ツ',
    'ヮ': 'ワ',
  };
  return smallToLarge[char] || char;
}

function hasOnlySmallKanaVariant(a, b) {
  if (a.length !== b.length) return false;
  const diffs = [];
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) diffs.push([a[i], b[i]]);
  }
  return diffs.length === 1 && normalizeSmallKana(diffs[0][0]) === normalizeSmallKana(diffs[0][1]);
}

function hasOnlyTrailingLongVowelDifference(shorter, longer) {
  return longer === `${shorter}ー`;
}

function getTagMergeReason(a, b) {
  if (!a.normalized || !b.normalized || a.normalized === b.normalized) {
    return '空白、大小文字、全角/半角などの表記ゆれがあります。';
  }

  const shorter = a.normalized.length <= b.normalized.length ? a.normalized : b.normalized;
  const longer = a.normalized.length > b.normalized.length ? a.normalized : b.normalized;
  const lengthDiff = longer.length - shorter.length;

  if (shorter.length >= 3 && lengthDiff === 1 && hasOnlyTrailingLongVowelDifference(shorter, longer)) {
    return '長音表記の有無だけが異なる表記ゆれがあります。';
  }

  if (longer.includes(shorter)) {
    return null;
  }

  const hasNumericDifference = /\d/.test(a.normalized) || /\d/.test(b.normalized);
  const distance = editDistance(a.normalized, b.normalized, true);
  if (!hasNumericDifference && shorter.length >= 4 && lengthDiff === 0 && distance === 1 && hasOnlySmallKanaVariant(a.normalized, b.normalized)) {
    return 'タグ名が非常に類似しており、表記ゆれの可能性があります。';
  }

  return null;
}

function chooseCanonicalTag(a, b) {
  if (a.count !== b.count) return a.count > b.count ? a : b;

  if (a.normalized === b.normalized) {
    const aHasKatakana = /[\u30A1-\u30FA]/.test(a.tag);
    const bHasKatakana = /[\u30A1-\u30FA]/.test(b.tag);
    const aHasHiragana = /[\u3041-\u3096]/.test(a.tag);
    const bHasHiragana = /[\u3041-\u3096]/.test(b.tag);
    if (aHasKatakana !== bHasKatakana && aHasHiragana !== bHasHiragana) {
      return aHasKatakana ? a : b;
    }

    const aCompactLength = a.tag.normalize('NFKC').replace(/[\s\u3000_\-/／|・.。]+/g, '').length;
    const bCompactLength = b.tag.normalize('NFKC').replace(/[\s\u3000_\-/／|・.。]+/g, '').length;
    const aRemovedChars = a.tag.normalize('NFKC').length - aCompactLength;
    const bRemovedChars = b.tag.normalize('NFKC').length - bCompactLength;
    if (aRemovedChars !== bRemovedChars) return aRemovedChars < bRemovedChars ? a : b;
  }

  const aContainsB = a.normalized.includes(b.normalized);
  const bContainsA = b.normalized.includes(a.normalized);
  if (aContainsB !== bContainsA) return aContainsB ? a : b;

  if (a.tag.length !== b.tag.length) return a.tag.length > b.tag.length ? a : b;
  return a.tag.localeCompare(b.tag, 'ja') <= 0 ? a : b;
}

function collectTagUsage({ notes = [], concepts = [], insights = [] } = {}) {
  const tagMap = new Map();

  const addTags = (docs, docType) => {
    docs.forEach((doc) => {
      (doc.tags || []).forEach((tag) => {
        if (typeof tag !== 'string' || tag.trim().length === 0) return;
        const cleanTag = tag.trim();
        const existing = tagMap.get(cleanTag) || {
          tag: cleanTag,
          normalized: normalizeTagForComparison(cleanTag),
          count: 0,
          noteIds: [],
          assetIds: [],
          insightIds: [],
        };
        existing.count += 1;
        if (doc?.id) {
          if (docType === 'note') existing.noteIds.push(doc.id);
          if (docType === 'asset') existing.assetIds.push(doc.id);
          if (docType === 'insight') existing.insightIds.push(doc.id);
        }
        tagMap.set(cleanTag, existing);
      });
    });
  };

  addTags(notes.filter(n => n.status !== 'archived'), 'note');
  addTags(concepts.filter(c => c.status !== 'archived' && c.status !== 'deprecated'), 'asset');
  addTags(insights.filter(i => i.status !== 'rejected'), 'insight');

  return [...tagMap.values()].filter(usage => usage.normalized.length >= 2);
}

/**
 * Detect tag merge candidates (Phase A: Rule-based)
 */
export function detectTagMergeCandidates({ notes = [], concepts = [], insights = [] } = {}) {
  const candidates = [];
  const tagUsages = collectTagUsage({ notes, concepts, insights });

  for (let i = 0; i < tagUsages.length; i++) {
    for (let j = i + 1; j < tagUsages.length; j++) {
      const tagA = tagUsages[i];
      const tagB = tagUsages[j];
      const reason = getTagMergeReason(tagA, tagB);
      if (!reason) continue;

      const canonical = chooseCanonicalTag(tagA, tagB);
      const source = canonical === tagA ? tagB : tagA;
      const affectedDocumentIds = {
        notes: [...new Set(source.noteIds)],
        assets: [...new Set(source.assetIds)],
        insights: [...new Set(source.insightIds)],
      };
      const affectedDocumentCount = affectedDocumentIds.notes.length +
        affectedDocumentIds.assets.length +
        affectedDocumentIds.insights.length;

      candidates.push({
        type: TAG_MERGE_TYPE,
        title: `タグ統合候補: 「${source.tag}」→「${canonical.tag}」`,
        summary: `${reason} 「${source.tag}」を「${canonical.tag}」へ統合する候補です。対象: ${affectedDocumentCount}件。`,
        targetType: 'tag',
        targetId: canonical.tag,
        proposedPatch: {
          oldTag: source.tag,
          newTag: canonical.tag,
          affectedDocumentCount,
          affectedDocumentIds,
          reason,
        }
      });
    }
  }

  return candidates;
}

export function getDuplicateEntityPool(targetType, { notes = [], concepts = [], insights = [] } = {}) {
  if (targetType === 'note') return notes;
  if (targetType === 'asset') return concepts;
  return insights;
}
